import { usePeopleManagementApi } from 'infrastructure/api';
import { useEmployeeId } from 'domain/people/employee/employeeIdentityRouting';
import { transformSkills, getValidSkillsConfig, mapToTargetFormat, findDuplicateIndexes } from './employeeSkillHelpers';
import { cloneDeep } from 'lodash';
import { InitSkillType, SkillInputConfig, SkillShowFormType } from './types';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import { DEFAULT_ERROR_MESSAGE } from 'framework/form';

export const CREATE_EMPLOYEE_SKILLS = 'CREATE_EMPLOYEE_SKILLS';
export const READ_EMPLOYEE_SKILLS = 'READ_EMPLOYEE_SKILLS_LIST';

const useEmployeeSkillsService = () => {
  const id = useEmployeeId()!;
  const { addMessage } = useMessageService();
  const { sendPostRequest } = usePeopleManagementApi();

  const getEmployeesSkillsById = async (employeeAlias: string): Promise<InitSkillType[]> => {
    const request = await sendPostRequest(`/employees/${employeeAlias}/skills/search`, {
      pageSize: 100,
    });
    const response = await request.json();
    return response.result;
  };

  const getSkillsShema = async (): Promise<SkillInputConfig[]> => {
    const request = await sendPostRequest(`/skills/search-configured`, {
      pageSize: 100,
    });

    const response = await request.json();
    return response.result;
  };

  const getAllData = async () => {
    try {
      const employeeDataResponse = await getEmployeesSkillsById(id);
      const schema = await getSkillsShema();

      return {
        skills: transformSkills(employeeDataResponse),
        skillsShema: getValidSkillsConfig(schema),
      };
    } catch (error) {
      addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
      return {
        skills: [],
        skillsShema: {
          skills: [],
        },
      };
    }
  };

  const submitSkills = async (requestData: any) => {
    const cloneRequestData = cloneDeep(requestData) as SkillShowFormType;

    const requestDataMap = mapToTargetFormat(cloneRequestData.skills);
    const dublicate = findDuplicateIndexes(requestDataMap);

    if (dublicate.length) {
      const errorResponse = new Response(
        JSON.stringify({
          errors: dublicate.reduce((obj, item) => {
            return {
              ...obj,
              ...item.reduce(
                (acc, itemError) => ({
                  ...acc,
                  ['skills.' + itemError + '.skillName']: 'Chosen skill already exist with same name or properties.',
                }),
                [],
              ),
            };
          }, {}),
        }),
        {
          status: 400,
          headers: { 'Content-Type': 'application/json' },
        },
      );
      throw errorResponse;
    }

    const request = await sendPostRequest(`/employees/${id}/skills/submit`, {
      skills: mapToTargetFormat(cloneRequestData.skills),
    });
    const response = await request.json();
    return {
      skills: transformSkills(response.skills as InitSkillType[]),
    };
  };

  return {
    submitSkills,
    getAllData,
  };
};

export default useEmployeeSkillsService;
